/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { Dialog, IconButton, Button, CircularProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { JobContext } from 'context/jobContextContainer';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { NotificationContext } from 'components/NotificationOverlay';
import {
  tripMonitorService,
  tripMonitorRequestState,
  TRIP_MONITOR_TARGET_LABEL,
} from 'services/tripMonitorService';

// Job status codes (see pergoapi/tools/constants.php).
const ACCEPT = 2;
const CONFIRM = 4;
const PICKUP = 5;

/**
 * Trip Monitor modal — dispatcher fix for a geofence exit-without-status event.
 * "Set ..." applies the missed milestone(s) server-side with the geofence-exit
 * point/time (MAS + Kinetik posts fire like a late button tap). No Show and
 * Cancel resolve the event through the same endpoint; Dismiss just closes it.
 */
const TripMonitorModal = ({ refreshTables, isCallCenter, callcenterActiveDb }) => {
  const {
    tripMonitorModalInfo,
    setTripMonitorModalInfo,
    tripMonitorAlerts,
    setTripMonitorAlerts,
  } = useContext(JobContext);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const { showNotification } = useContext(NotificationContext);
  // Two-step guard for the destructive actions: first click arms, second confirms.
  const [armedAction, setArmedAction] = useState(null);

  useEffect(() => {
    setArmedAction(null);
  }, [tripMonitorModalInfo ? tripMonitorModalInfo.id : null]);

  const close = () => setTripMonitorModalInfo(null);

  if (!tripMonitorModalInfo) {
    return null;
  }

  const alert = tripMonitorModalInfo;
  const jobId = alert.job_id;
  const isPickup = alert.event_type === 'pickup_exit';
  const jobStatus = parseInt(alert.job_status, 10);
  const missedArrive = isPickup && jobStatus < CONFIRM;
  const missedLabel = isPickup ? (missedArrive ? 'Arrive + Pick Up' : 'Pick Up') : 'Drop Off';
  // The exact status changes the driver skipped — shown in the warning note.
  const missedStatusLabels = isPickup
    ? (missedArrive ? ['Arrived', 'Picked Up'] : ['Picked Up'])
    : ['Dropped Off'];
  const where = isPickup ? 'pickup' : 'drop-off';
  // Server-formatted in the base's timezone (event_time itself is UTC-basis).
  const eventTime = (alert.event_time_display || '').trim();

  // App-driven fix state: 'none' | 'waiting' (request sent, app updating) | 'timeout'.
  const requestState = tripMonitorRequestState(alert);
  const requestedLabel = TRIP_MONITOR_TARGET_LABEL[parseInt(alert.requested_status, 10)] || missedLabel;
  const retryAction = parseInt(alert.requested_status, 10) === 9 ? 'noshow' : 'set_status';

  // Milestone strip: solid = reached, dashed = missed, grey = not yet due.
  const steps = [
    { label: 'Accepted', state: jobStatus >= ACCEPT ? 'done' : 'future' },
    { label: 'Arrived', state: jobStatus >= CONFIRM ? 'done' : isPickup ? 'missed' : 'future' },
    { label: 'Pick Up', state: jobStatus >= PICKUP ? 'done' : isPickup ? 'missed' : 'future' },
    { label: 'Drop Off', state: !isPickup ? 'missed' : 'future' },
  ];

  // Page through all pending events without closing the modal.
  const alerts = Array.isArray(tripMonitorAlerts) ? tripMonitorAlerts : [];
  const currentIndex = Math.max(0, alerts.findIndex(a => String(a.id) === String(alert.id)));
  const total = alerts.length;
  const goAlert = delta => {
    if (total <= 1) return;
    setTripMonitorModalInfo(alerts[(((currentIndex + delta) % total) + total) % total]);
  };

  const advanceOrClose = remaining => {
    setTripMonitorAlerts(remaining);
    if (remaining.length > 0) {
      setTripMonitorModalInfo(remaining[Math.min(currentIndex, remaining.length - 1)]);
    } else {
      close();
    }
  };

  const onResolve = async action => {
    const labels = {
      set_status: `Setting ${missedLabel}`,
      noshow: 'Marking No Show',
      cancel: 'Cancelling job',
      dismiss: 'Dismissing',
    };
    showCircularProgress(labels[action] || 'Processing');
    try {
      const res = await tripMonitorService.resolve_trip_monitor_alert(alert.id, action, {
        ...(isCallCenter && { dbname: callcenterActiveDb }),
      });
      hideCircularProgress();
      if (res && res.data && res.data.status === 'SUCCESS') {
        const { alerts: freshAlerts, resolved, requested } = res.data.data || {};
        if (requested) {
          // App-driven fix: the driver's app applies the status through its normal
          // flow. The event stays visible as "updating…" until the poll sees it.
          const targetLabel = action === 'noshow' ? 'No Show' : missedLabel;
          showNotification(`Sent to driver's app — Job #${jobId} updating to ${targetLabel}…`, 'success');
          setTripMonitorAlerts(Array.isArray(freshAlerts) ? freshAlerts : alerts);
          close();
          return;
        }
        if ((action === 'set_status' || action === 'noshow') && !resolved) {
          showNotification(`Could not reach the driver's app for Job #${jobId} — please retry or call the driver.`, 'error');
          setTripMonitorAlerts(Array.isArray(freshAlerts) ? freshAlerts : alerts);
          return;
        }
        if (resolved && action !== 'dismiss') {
          showNotification(`Job #${jobId} cancelled`, 'success');
        }
        if (refreshTables) refreshTables();
        advanceOrClose(Array.isArray(freshAlerts) ? freshAlerts : alerts.filter(a => a.id !== alert.id));
      } else {
        showNotification('Sorry, failed to process — please retry.', 'error');
      }
    } catch (e) {
      hideCircularProgress();
      showNotification('Sorry, failed to process — please retry.', 'error');
    }
  };

  const armOrRun = action => {
    if (armedAction === action) {
      setArmedAction(null);
      onResolve(action);
    } else {
      setArmedAction(action);
    }
  };

  return (
    <StyledDialog open onClose={close} maxWidth="sm" fullWidth>
      <div className="tm-head">
        <div>
          <div className="tm-eyebrow">Trip Monitor</div>
          <div className="tm-title">
            {`Job #${jobId}`}
            {alert.passenger_name ? ` · ${alert.passenger_name}` : ''}
          </div>
        </div>
        {total > 1 && (
          <div className="tm-pager">
            <button type="button" onClick={() => goAlert(-1)} aria-label="previous">‹</button>
            {`${currentIndex + 1} of ${total}`}
            <button type="button" onClick={() => goAlert(1)} aria-label="next">›</button>
          </div>
        )}
        <IconButton size="small" onClick={close} style={{ color: '#8aa0b6' }} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className="tm-body">
        <dl className="tm-kv">
          <dt>Driver</dt>
          <dd>{[alert.driver_name, alert.vehicle_no ? `Car ${alert.vehicle_no}` : null].filter(Boolean).join(' · ') || '—'}</dd>
          <dt>Pickup</dt>
          <dd>{alert.start_addr || '—'}</dd>
          <dt>Drop-off</dt>
          <dd>{alert.end_addr || '—'}</dd>
          {alert.mas_invoice_number ? (
            <>
              <dt>Broker</dt>
              <dd>{`MAS · invoice ${alert.mas_invoice_number}`}</dd>
            </>
          ) : alert.kinetik_trip_id ? (
            <>
              <dt>Broker</dt>
              <dd>Kinetik</dd>
            </>
          ) : null}
        </dl>

        <div className="tm-steps">
          {steps.map(s => (
            <div className={`tm-step ${s.state}`} key={s.label}>
              <span className="tm-dot" />
              <small>{s.label}</small>
            </div>
          ))}
        </div>

        <div className="tm-missed-note">
          <span className="tm-warn">!</span>
          <span>
            {`Vehicle left the ${where} area${eventTime ? ` at ${eventTime}` : ''} without marking `}
            {missedStatusLabels.map((label, index) => (
              <React.Fragment key={label}>
                {index > 0 ? ' and ' : ''}
                <strong>{`“${label}”`}</strong>
              </React.Fragment>
            ))}
            {'. '}
            {alert.mas_invoice_number
              ? `MAS will not receive ${missedStatusLabels.length > 1 ? 'these milestones' : `the ${isPickup ? 'pickup' : 'drop-off'} milestone`} until this is fixed.`
              : `The trip record is missing ${missedStatusLabels.length > 1 ? 'these milestones' : 'this milestone'} until it is fixed.`}
          </span>
        </div>

        {requestState === 'waiting' && (
          <div className="tm-request-note waiting">
            <CircularProgress size={16} style={{ color: '#2f6df6' }} />
            <span>
              {`Sent to the driver's app — updating to `}
              <strong>{requestedLabel}</strong>
              {`… The status will refresh here once the app confirms.`}
            </span>
          </div>
        )}
        {requestState === 'timeout' && (
          <div className="tm-request-note timeout">
            <span className="tm-warn">!</span>
            <span>
              {`The driver's app has not responded to the `}
              <strong>{requestedLabel}</strong>
              {` request. Retry, or call the driver — the phone may be offline or the app closed.`}
            </span>
          </div>
        )}
      </div>

      <div className="tm-foot">
        {requestState === 'none' && (
          <Button variant="contained" onClick={() => onResolve('set_status')} className="tm-btn-primary">
            {`Set ${missedLabel}`}
          </Button>
        )}
        {requestState === 'timeout' && (
          <Button variant="contained" onClick={() => onResolve(retryAction)} className="tm-btn-primary">
            {`Retry ${requestedLabel}`}
          </Button>
        )}
        {requestState !== 'waiting' && (
          <>
            {isPickup && (
              <Button onClick={() => armOrRun('noshow')} className={`tm-btn-ghost warn ${armedAction === 'noshow' ? 'armed' : ''}`}>
                {armedAction === 'noshow' ? 'Confirm No Show?' : 'No Show'}
              </Button>
            )}
            <Button onClick={() => armOrRun('cancel')} className={`tm-btn-ghost warn ${armedAction === 'cancel' ? 'armed' : ''}`}>
              {armedAction === 'cancel' ? 'Confirm Cancel?' : 'Cancel Trip'}
            </Button>
          </>
        )}
        <Button onClick={() => onResolve('dismiss')} className="tm-btn-ghost">
          Dismiss
        </Button>
      </div>
    </StyledDialog>
  );
};

export default TripMonitorModal;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 12px;
    overflow: hidden;
  }

  .tm-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px 14px 20px;
    background: #0d1b2a;
    color: #e8eef5;

    > div:first-child {
      flex: 1;
    }
  }

  .tm-eyebrow {
    font-size: 11px;
    letter-spacing: 1.5px;
    font-weight: 700;
    color: #ff8a3d;
  }

  .tm-title {
    font-size: 16px;
    font-weight: 700;
  }

  .tm-pager {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #8aa0b6;
    font-size: 12.5px;
    white-space: nowrap;

    button {
      background: none;
      border: 1px solid #2a3a4d;
      color: #8aa0b6;
      border-radius: 5px;
      padding: 2px 8px;
      cursor: pointer;
    }
  }

  .tm-body {
    padding: 18px 20px 6px;
  }

  .tm-kv {
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: 5px 14px;
    font-size: 14px;
    margin: 0 0 16px;

    dt {
      color: #8a97a5;
    }
    dd {
      margin: 0;
    }
  }

  .tm-steps {
    display: flex;
    margin: 2px 0 16px;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .tm-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    min-width: 86px;
    position: relative;

    &:not(:first-child)::before {
      content: '';
      position: absolute;
      top: 6px;
      right: 50%;
      width: 100%;
      height: 2px;
      background: #dde4ec;
    }

    .tm-dot {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #2e9e5b;
      position: relative;
      z-index: 2;
    }

    small {
      font-size: 11.5px;
      color: #5a6c7d;
      white-space: nowrap;
    }

    &.missed .tm-dot {
      background: #fff;
      border: 2.5px dashed #ff8a3d;
      width: 13px;
      height: 13px;
    }

    &.missed small {
      color: #e07520;
      font-weight: 700;
    }

    &.future .tm-dot {
      background: #dde4ec;
    }
  }

  .tm-missed-note {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 12px 14px;
    border-radius: 8px;
    background: #fff3e8;
    border: 1px solid #f5c9a1;
    font-size: 13.5px;
    margin-bottom: 12px;

    .tm-warn {
      color: #e07520;
      font-weight: 800;
    }
  }

  .tm-request-note {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 12px 14px;
    border-radius: 8px;
    font-size: 13.5px;
    margin-bottom: 12px;

    &.waiting {
      background: #eef4ff;
      border: 1px solid #bcd2f7;
    }

    &.timeout {
      background: #fdeaea;
      border: 1px solid #f0b6b6;

      .tm-warn {
        color: #c0392b;
        font-weight: 800;
      }
    }
  }

  .tm-foot {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    flex-wrap: wrap;
    padding: 12px 20px 16px;
    border-top: 1px solid #e7ecf2;
    background: #f6f8fb;
  }

  .tm-btn-ghost {
    color: #5a6c7d;
    border: 1px solid #d5dde6;

    &.warn {
      color: #c26a1f;
      border-color: #ecc9a4;
    }

    &.warn.armed {
      background: #ffe9d6;
      font-weight: 700;
    }
  }

  .tm-btn-primary {
    background: #2f6df6;
    color: #fff;

    &:hover {
      background: #2559ce;
    }

    .tm-btn-time {
      opacity: 0.75;
      font-weight: 400;
    }
  }
`;
